import { Search, SlidersHorizontal, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAccountCurrency } from "@/auth/auth-context";
import { getUserFacingError } from "@/lib/api/errors";
import { api } from "@/lib/api/services";
import { formatMoney } from "@/lib/formatters";
import { useApiResource } from "@/lib/hooks/use-api-resource";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import { matchCollections, parseSearchQuery } from "@/lib/nft-search";
import { useT } from "@/lib/i18n";
import { LoadingState } from "./ui/page-state";
import { SafeMedia } from "./ui/safe-media";
import { OptionSwitch } from "./option-switch";

type SearchMode = "all" | "price" | "number";

export function MarketplaceSearch() {
  const t = useT();
  const currency = useAccountCurrency();
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState<SearchMode>("all");
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const [searchLocationKey, setSearchLocationKey] = useState(location.key);
  if (searchLocationKey !== location.key) {
    setSearchLocationKey(location.key);
    setOpen(false);
    setQuery("");
    setMode("all");
  }
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debouncedQuery = useDebouncedValue(query.trim(), 350);
  const parsed = useMemo(() => parseSearchQuery(debouncedQuery), [debouncedQuery]);
  const queryReady = query.trim() === debouncedQuery;
  const hasSearchQuery = debouncedQuery.length > 0 && (parsed.text !== "" || parsed.number !== null);
  const currentQuery = parseSearchQuery(query);
  const showResults = open && (currentQuery.text !== "" || currentQuery.number !== null);
  const requestReady = showResults && queryReady && hasSearchQuery;
  const search = useApiResource(requestReady ? `search:${debouncedQuery}` : null, (signal) => api.search(debouncedQuery, signal));
  // The search API returns only NFTs; collection links still use get_collections.
  const collections = useApiResource(showResults ? "search-collections" : null, (signal) => api.getCollections(signal));
  const collectionList = collections.status === "success" ? collections.data : null;
  const matches = useMemo(() => ({ collections: collectionList ? matchCollections(collectionList, parsed.text).slice(0, 4) : [] }), [collectionList, parsed.text]);
  const nftHits = (search.status === "success" ? search.data : []).slice(0, 6);
  const nftLoading = search.status === "loading" || (search.status === "success" && nftHits.length === 0 && collections.status === "loading");
  const nftError = search.status === "error" ? search.error : null;
  const hasEntities = matches.collections.length > 0;
  const modeOptions = [
    { id: "all", label: t("header.search_all") },
    { id: "price", label: t("header.search_price") },
    { id: "number", label: t("header.search_number") }
  ];
  const hint = t(mode === "number" ? "header.search_number_hint" : mode === "price" ? "header.search_price_hint" : "header.search_all_hint");

  function selectMode(id: string) {
    if (id !== "all" && id !== "price" && id !== "number") return;
    setMode(id);
    if (id === "number") {
      const number = parseSearchQuery(query).number ?? (/^\d+$/.test(query.trim()) ? query.trim() : "");
      setQuery(`#${number}`);
    } else if (id === "price") {
      const price = query.replace(/^#\s*/, "").trim();
      setQuery(/^\d*(?:[.,]\d*)?$/.test(price) ? price : "");
    }
    inputRef.current?.focus();
  }

  function changeQuery(value: string) {
    if (mode === "number") setQuery(`#${value.replace(/\D/g, "")}`);
    else if (mode === "price") {
      if (/^\d*(?:[.,]\d*)?$/.test(value)) setQuery(value);
    } else setQuery(value);
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (event.target instanceof Node && !containerRef.current?.contains(event.target)) setOpen(false);
    };
    const closeWithEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") { inputRef.current?.focus(); setOpen(false); }
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", closeWithEscape);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", closeWithEscape);
    };
  }, [open]);

  return (
    <div className={`search-box market-search app-header__search${query && query !== "#" ? " market-search--has-query" : ""}${open ? " market-search--open" : ""}`} ref={containerRef}>
      <Search size={18} aria-hidden="true" />
      <input
        ref={inputRef}
        role="combobox"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? "market-search-results" : undefined}
        aria-describedby={open ? "market-search-hint" : undefined}
        inputMode={mode === "number" ? "numeric" : mode === "price" ? "decimal" : "text"}
        onKeyDown={(event) => { if (event.key === "ArrowDown" && showResults) { event.preventDefault(); containerRef.current?.querySelector<HTMLElement>(".search-result")?.focus(); } }}
        className="field__input"
        value={query}
        onChange={(event) => changeQuery(event.target.value)}
        onFocus={() => setOpen(true)}
        placeholder={t(mode === "price" ? "header.search_price_placeholder" : "header.search_placeholder")}
        aria-label={t("market.search_aria")}
      />
      {query && query !== "#" ? <button type="button" className="search-clear" aria-label={t("market.search_clear")} onClick={() => { setQuery(mode === "number" ? "#" : ""); inputRef.current?.focus(); }}><X size={16} /></button> : null}
      <button type="button" className="search-filter-toggle" aria-label={t("header.search_mode")} aria-expanded={open} aria-controls={open ? "market-search-results" : undefined} onMouseDown={(event) => event.preventDefault()} onClick={() => setOpen((current) => !current)}><SlidersHorizontal size={18} aria-hidden="true" /></button>
      {open ? (
        <div id="market-search-results" className="search-results" role="dialog" aria-label={t("market.search_results")}>
          <div className="search-filters">
            <OptionSwitch label={t("header.search_mode")} options={modeOptions} value={mode} onSelect={selectMode} />
            <p id="market-search-hint" className="sr-only">{hint}</p>
          </div>
          {!showResults ? <div className="search-examples">
            <p>{t("header.search_examples")}</p>
            <div className="search-examples__list">
              <button type="button" onClick={() => { setMode("all"); setQuery("Steamboat Willie"); inputRef.current?.focus(); }}>Steamboat Willie</button>
              {currency ? <button type="button" onClick={() => { setMode("price"); setQuery("100"); inputRef.current?.focus(); }}>{formatMoney(100, currency)}</button> : null}
              <button type="button" onClick={() => { setMode("number"); setQuery("#1547"); inputRef.current?.focus(); }}>#1547</button>
            </div>
          </div> : null}
          {showResults ? !queryReady ? <LoadingState label={t("market.searching")} /> : <>
          {matches.collections.length ? <div className="search-group"><h3>{t("market.collections")}</h3>{matches.collections.map((item) => (
            <Link viewTransition to={`/client/collection/${item.id}`} className="search-result" key={`c-${String(item.id)}`} onClick={() => setOpen(false)}>
              <SafeMedia src={item.image} alt="" /><div className="search-result__body"><strong>{item.name}</strong><span>{item.blockchain}</span></div>
            </Link>))}</div> : null}
          <div className="search-group">{nftHits.length > 0 ? <h3>NFT</h3> : null}
          {nftHits.map((item) => (
            <Link viewTransition to={`/client/collectible/${item.id}`} className="search-result" key={String(item.id)} onClick={() => setOpen(false)}>
              <SafeMedia src={item.image} alt={`${item.collection_name} #${item.number}`} />
              <div className="search-result__body"><strong>{item.collection_name} #{item.number}</strong><span>{item.blockchain}</span></div>
              <span className="search-result__price">{formatMoney(item.price, item.currency)}</span>
            </Link>
          ))}
          {nftLoading ? <LoadingState label={t("market.searching")} /> : nftError ? <div className="search-message" role="alert">{getUserFacingError(nftError)}</div> : nftHits.length === 0 && !hasEntities ? <div className="search-message">{t("market.nothing_found")}</div> : null}
          </div>
          </> : null}
        </div>
      ) : null}
    </div>
  );
}
